import { noop } from "lodash";
import { usePage } from "PFApp/use_page";
import { fetchAccount, fetchCustomization, updateCustomization } from "PFCore/services/accounts/account";
import { Feature } from "PFTypes/features";
import { useEffect } from "react";

import { AccountCustomization } from "./account_customization";

type InitPage = {
  features: Feature[] | null;
  customization: AccountCustomization | null;
  accountName: string;
  fetch: Function | null;
  loaded: boolean;
  errors: string[];
  saving: boolean;
  putCustomization: () => void;
};

const initPage: InitPage = {
  features: null,
  customization: null,
  accountName: "",
  fetch: null,
  loaded: false,
  errors: [],
  saving: false,
  putCustomization: noop
};

export const useCustomizationPage = (accountId: string) => {
  const { page, setPage, mergePage, resetPage } = usePage<InitPage>(`features`, initPage);

  const fetch = () =>
    fetchCustomization(accountId)
      .then((response) =>
        mergePage({
          loaded: true,
          customization: formatResponse(response)
        })
      )
      .catch(() =>
        mergePage({
          loaded: true,
          errors: ["there was an error"]
        })
      );

  const getAccount = () =>
    fetchAccount(accountId)
      .then(({ name }) =>
        mergePage({
          accountName: name
        })
      )
      .catch(() =>
        mergePage({
          loaded: true,
          errors: ["there was an error"]
        })
      );

  const putCustomization = (data) => {
    mergePage({ saving: true });

    return updateCustomization(accountId, data)
      .then((response) => {
        mergePage({
          loaded: true,
          saving: false,
          customization: response as AccountCustomization
        });
        return true;
      })
      .catch(() => {
        mergePage({
          loaded: true,
          saving: false,
          errors: ["there was an error"]
        });
        return false;
      });
  };

  // Each custom link needs a unique id to prevent react rendering them incorrectly on remove
  const formatResponse = (response: AccountCustomization) => {
    const startId = Date.now();
    response.customLinks = response.customLinks.map((customLink, index) => {
      customLink.id = startId + index;
      return customLink;
    });
    return response;
  };

  useEffect(() => {
    setPage(initPage);
    return resetPage;
  }, []);

  useEffect(() => {
    getAccount();
    fetch();
  }, []);

  return { ...page, putCustomization };
};
